import { useState } from "react";
import { ExternalLink, Trophy } from "lucide-react";
import {
  SEASON_LABEL,
  SEASON_SOURCE_URL,
  TEAM_LEADERS_2025,
  PASSING_2025,
  RUSHING_2025,
  RECEIVING_2025,
  DEFENSE_2025,
  SCORING_2025,
  espnHeadshot,
} from "@/data/stats2025";

const TABS = [
  { k: "passing",   label: "Passing",   data: PASSING_2025 },
  { k: "rushing",   label: "Rushing",   data: RUSHING_2025 },
  { k: "receiving", label: "Receiving", data: RECEIVING_2025 },
  { k: "defense",   label: "Defense",   data: DEFENSE_2025 },
  { k: "scoring",   label: "Scoring",   data: SCORING_2025 },
];

function PlayerAvatar({ espnId, name }) {
  const [errored, setErrored] = useState(false);
  if (errored) {
    return (
      <div
        className="bg-titans-elev flex items-center justify-center font-display text-titans-blue uppercase w-full h-full"
        aria-label={name}
      >
        {name
          .split(" ")
          .map((s) => s[0])
          .join("")
          .slice(0, 2)}
      </div>
    );
  }
  return (
    <img
      src={espnHeadshot(espnId, 200)}
      alt={name}
      loading="lazy"
      onError={() => setErrored(true)}
      className="w-full h-full object-cover"
    />
  );
}

function LeaderCard({ leader, rank }) {
  return (
    <div
      data-testid={`stats-2025-leader-${leader.key}`}
      className="relative titans-card overflow-hidden border-l-4 border-titans-red"
    >
      <div
        className={`absolute inset-0 bg-gradient-to-br ${leader.accent} pointer-events-none`}
        aria-hidden
      />
      <div className="relative p-4 md:p-5 flex items-center gap-4">
        <div className="shrink-0 w-20 h-20 md:w-24 md:h-24 border-2 border-titans-blue overflow-hidden bg-titans-navy">
          <PlayerAvatar espnId={leader.espnId} name={leader.name} />
        </div>
        <div className="min-w-0">
          <div className="font-cond uppercase tracking-[0.25em] text-[10px] text-titans-blue">
            {leader.label}
          </div>
          <div className="font-display text-3xl md:text-4xl text-white leading-none mt-1">
            {leader.value}
          </div>
          <div className="font-display text-base md:text-lg uppercase mt-2 truncate">
            {leader.name}
          </div>
          <div className="font-cond uppercase tracking-wider text-xs text-titans-silver">
            {leader.pos}
          </div>
        </div>
        <div
          className="absolute top-2 right-3 font-display text-3xl text-titans-blue/40"
          aria-hidden
        >
          {rank}
        </div>
      </div>
    </div>
  );
}

function StatsTable({ data, testidPrefix }) {
  return (
    <div
      className="titans-card overflow-x-auto"
      data-testid={`${testidPrefix}-table`}
    >
      <table className="w-full min-w-[760px]">
        <thead className="bg-titans-elev">
          <tr className="text-left font-cond uppercase tracking-wider text-[11px] text-titans-silver">
            <th className="px-4 py-3 sticky left-0 bg-titans-elev z-[1]">Player</th>
            {data.headers.map((h) => (
              <th key={h} className="px-3 py-3 text-right whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row) => (
            <tr
              key={row.name}
              className="border-t border-[rgba(138,141,143,0.15)] hover:bg-titans-elev/40 transition-colors"
              data-testid={`${testidPrefix}-row-${row.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <td className="px-4 py-3 sticky left-0 bg-titans-void z-[1]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 border border-titans-blue/40 overflow-hidden bg-titans-navy shrink-0">
                    <PlayerAvatar espnId={row.espnId} name={row.name} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display text-sm md:text-base uppercase whitespace-nowrap">{row.name}</div>
                    <div className="font-cond uppercase tracking-wider text-[10px] text-titans-blue">{row.pos}</div>
                  </div>
                </div>
              </td>
              {row.stats.map((v, idx) => (
                <td
                  key={`${row.name}-${data.headers[idx]}`}
                  className="px-3 py-3 text-right font-cond text-sm text-titans-silver whitespace-nowrap"
                >
                  {v}
                </td>
              ))}
            </tr>
          ))}
          {data.totals && (
            <tr className="border-t-2 border-titans-blue bg-titans-elev/60">
              <td className="px-4 py-3 sticky left-0 bg-titans-elev z-[1] font-cond uppercase tracking-wider text-titans-blue">
                {data.totals.label}
              </td>
              {data.totals.stats.map((v, idx) => (
                <td
                  key={`team-${data.headers[idx]}`}
                  className="px-3 py-3 text-right font-display text-sm text-white whitespace-nowrap"
                >
                  {v}
                </td>
              ))}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function SeasonStats2025() {
  const [tab, setTab] = useState("passing");
  const active = TABS.find((t) => t.k === tab) ?? TABS[0];
  return (
    <section data-testid="stats-2025-section" className="mt-16">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Trophy size={14} className="text-titans-red" />
            <span className="font-cond uppercase tracking-[0.3em] text-[10px] text-titans-red">
              2025 Regular Season
            </span>
          </div>
          <h2
            className="font-display text-3xl md:text-5xl uppercase tracking-wide section-title"
            data-testid="stats-2025-heading"
          >
            {SEASON_LABEL}
          </h2>
          <p className="text-titans-silver font-body text-sm mt-2 max-w-2xl">
            Team leaders and full positional splits for the 2025 regular season — final numbers.
          </p>
        </div>
        <a
          href={SEASON_SOURCE_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="stats-2025-source-link"
          className="inline-flex items-center gap-2 text-titans-blue hover:text-white border-b border-titans-blue/60 hover:border-white font-cond uppercase tracking-wider text-xs pb-0.5 self-start md:self-auto"
        >
          Source: ESPN <ExternalLink size={12} />
        </a>
      </div>

      {/* Marquee leader cards */}
      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4 mb-10"
        data-testid="stats-2025-leaders-grid"
      >
        {TEAM_LEADERS_2025.map((leader, i) => (
          <LeaderCard key={leader.key} leader={leader} rank={i + 1} />
        ))}
      </div>

      {/* Tabbed split tables */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="stats-2025-tabs">
        {TABS.map((t) => {
          const isActive = t.k === tab;
          return (
            <button
              key={t.k}
              type="button"
              onClick={() => setTab(t.k)}
              data-testid={`stats-2025-tab-${t.k}`}
              className={`font-cond uppercase tracking-wider text-xs md:text-sm px-4 py-2 transition-colors ${
                isActive
                  ? "bg-titans-blue text-white"
                  : "border border-[rgba(138,141,143,0.3)] text-titans-silver hover:text-white hover:border-titans-blue"
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      <StatsTable data={active.data} testidPrefix={`stats-2025-${active.k}`} />

      <p className="text-titans-muted text-xs italic mt-3">
        Player headshots and statistics courtesy of{" "}
        <a
          href={SEASON_SOURCE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-titans-blue hover:text-white"
        >
          ESPN
        </a>
        . The Titan Standard is an independent fan publication and is not affiliated with ESPN, the Tennessee Titans, or the NFL.
      </p>
    </section>
  );
}
